import { useState, useCallback, useRef, useEffect } from 'react';
import type { Order } from '@/services/orders';
import { Card } from './Card';

interface AdminNotesCardProps {
  readonly order: Order;
  readonly onSave: (notes: string | null) => void;
  readonly isPending: boolean;
}

export const AdminNotesCard = ({ order, onSave, isPending }: AdminNotesCardProps) => {
  const [value, setValue] = useState(order.adminNotes ?? '');
  const [saved, setSaved] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync if order notes change externally
  useEffect(() => {
    setValue(order.adminNotes ?? '');
  }, [order.adminNotes]);

  const handleChange = useCallback(
    (newValue: string) => {
      setValue(newValue);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        onSave(newValue.trim() || null);
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
      }, 500);
    },
    [onSave],
  );

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  return (
    <Card>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
        <div style={{ fontSize: '14px', fontWeight: 'var(--font-weight-medium)', color: 'var(--text-primary)' }}>
          Admin notes
        </div>
        {(saved || isPending) && (
          <span style={{ fontSize: '11px', color: saved ? 'var(--color-success-600)' : 'var(--text-tertiary)' }}>
            {isPending ? 'Saving...' : 'Saved'}
          </span>
        )}
      </div>
      <textarea
        value={value}
        onChange={(e) => handleChange(e.target.value)}
        placeholder="Click to add a note..."
        className="admin-input"
        style={{
          width: '100%',
          minHeight: '60px',
          fontSize: '13px',
          resize: 'vertical',
          fontFamily: 'inherit',
        }}
      />
    </Card>
  );
};

// Customer notes card (read-only)

interface CustomerNotesCardProps {
  readonly notes: string | null;
}

export const CustomerNotesCard = ({ notes }: CustomerNotesCardProps) => (
  <Card>
    <div
      style={{
        fontSize: '14px',
        fontWeight: 'var(--font-weight-medium)',
        color: 'var(--text-primary)',
        marginBottom: '8px',
      }}
    >
      Customer notes
    </div>
    {notes ? (
      <div style={{ fontSize: '13px', color: 'var(--text-secondary)', whiteSpace: 'pre-wrap' }}>
        {notes}
      </div>
    ) : (
      <div style={{ fontSize: '13px', color: 'var(--text-tertiary)', fontStyle: 'italic' }}>
        No customer notes on this order
      </div>
    )}
  </Card>
);
